import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import type { SubscriptionItemModel } from '@models/subscriptionItem'
import type { AppDispatch } from '@redux/store'
import type {
  Action, ActionResult, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/subscriptionItem/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/subscriptionItem/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/subscriptionItem/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/subscriptionItem/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/subscriptionItem/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/subscriptionItem/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/subscriptionItem/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/subscriptionItem/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/subscriptionItem/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/subscriptionItem/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/subscriptionItem/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/subscriptionItem/DELETE_FAILURE'

export type SubscriptionItemModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

const initialState: SubscriptionItemModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}): Action {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess(): Action {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []): Action {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(): Action {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(): Action {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []): Action {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(): Action {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(): Action {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []): Action {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(): Action {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(): Action {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []): Action {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadSubscriptionItems(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState): Promise<ActionResult<SubscriptionItemModel[]>> => {
    const loadedForKeys = [...getState().subscriptionItems.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/subscription-items', options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.SUBSCRIPTION_ITEM_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function loadSubscriptionItem(subscriptionItemId: number, options: EntityOptions = {}) {
  return (dispatch: AppDispatch, getState: () => RootReducerState): Promise<ActionResult<SubscriptionItemModel>> => {
    const state = getState()
    const loadedIds = [...state.subscriptionItems.loadedIds]

    if (subscriptionItemId && !loadedIds.includes(subscriptionItemId)){
      loadedIds.push(subscriptionItemId)
    }

    dispatch(fetchRequest({ loadedIds }))

    const promise = api(`/subscription-items/${subscriptionItemId}`, options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.SUBSCRIPTION_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))
        return { success: false, errors }
      })

    return promise
  }
}

export function createSubscriptionItem(subscriptionItem: SubscriptionItemModel, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify(subscriptionItem),
  }

  return (dispatch: AppDispatch): Promise<ActionResult<SubscriptionItemModel>> => {
    dispatch(createRequest())

    const promise = api('/subscription-items', options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.SUBSCRIPTION_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function updateSubscriptionItem(subscriptionItem: SubscriptionItemModel, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
    data: JSON.stringify(subscriptionItem),
  }

  return (dispatch: AppDispatch): Promise<ActionResult<SubscriptionItemModel>> => {
    dispatch(updateRequest())

    const promise = api(`/subscription-items/${subscriptionItem.id}`, options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.SUBSCRIPTION_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function deleteSubscriptionItem(subscriptionItem: SubscriptionItemModel) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch): Promise<ActionResult<SubscriptionItemModel>> => {
    dispatch(deleteRequest())

    const promise = api(`/subscription-items/${subscriptionItem.id}`, {}, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.SUBSCRIPTION_ITEM)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false, errors }
      })
    return promise
  }
}

// Reducer
export default function reducer(
  state: SubscriptionItemModuleState = initialState,
  action: Action = { type: '' },
): SubscriptionItemModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
